import { deleteWorkflowTemplate } from '@commenda/api';
import { Button, DataTable, useToggle } from '@commenda/kakashi';
import {
  IWorkflowTemplate,
  IWorkflowTemplateListResponse,
  TWorkflowTemplateCategory,
} from '@commenda/types';
import {
  DeleteConfirmationModal,
  UpdateWorkflowTemplateModal,
  useNotification,
} from '@commenda/ui';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import { buildWorkflowListColumns } from './buildWorkflowListColumns';
import { CreateWorkflowModal } from './CreateWorkflowModal';
import { EditWorkflowMetadataModal } from './EditWorkflowMetadataModal';
import { WorkflowTemplateMetadataHelper } from './workflow-template-metadata';
import { WorkflowListFilters } from './WorkflowListFilters';

interface WorkflowListPageProps {
  /** Overrides for surfaces (the affiliate console) whose editor lives on another route. Defaults
   * keep the client app's /workflow-builder destinations unchanged. `getWorkflowHref` may return
   * undefined for a workflow with nowhere to go (no version yet) — the row click then does
   * nothing instead of navigating to the page it is already on. */
  getWorkflowHref?: (workflowTemplateId: string) => string | undefined;
  getEditorHref?: (versionId: string) => string;
  workflows: IWorkflowTemplate[];
  mutate: KeyedMutator<IWorkflowTemplateListResponse>;
  /** Metadata columns, category/tag filter bars, and the Organize action (the affiliate console).
   * Default off keeps the client app's table unchanged. */
  showMetadataControls?: boolean;
}

export const WorkflowListPage = ({
  workflows,
  mutate,
  getWorkflowHref,
  getEditorHref,
  showMetadataControls = false,
}: WorkflowListPageProps) => {
  const router = useRouter();
  const { setNotification } = useNotification();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const deleteModalVisibility = useToggle();
  const updateModalVisibility = useToggle();
  const metadataModalVisibility = useToggle();

  const [selectedWorkflow, setSelectedWorkflow] =
    useState<IWorkflowTemplate | null>(null);
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] =
    useState<TWorkflowTemplateCategory | null>(null);

  const allTags = useMemo(
    () =>
      [
        ...new Set(
          workflows.flatMap((workflow) =>
            WorkflowTemplateMetadataHelper.getTags(workflow)
          )
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [workflows]
  );

  const presentCategories = useMemo(
    () => [
      ...new Set(
        workflows
          .map((workflow) =>
            WorkflowTemplateMetadataHelper.getCategory(workflow)
          )
          .filter((category): category is NonNullable<typeof category> =>
            Boolean(category)
          )
      ),
    ],
    [workflows]
  );

  const visibleWorkflows = useMemo(() => {
    if (!showMetadataControls) return workflows;
    return workflows.filter((workflow) => {
      if (
        selectedCategoryFilter !== null &&
        WorkflowTemplateMetadataHelper.getCategory(workflow) !==
          selectedCategoryFilter
      ) {
        return false;
      }
      const tags = WorkflowTemplateMetadataHelper.getTags(workflow);
      return selectedTagFilters.every((tag) => tags.includes(tag));
    });
  }, [
    workflows,
    showMetadataControls,
    selectedTagFilters,
    selectedCategoryFilter,
  ]);

  const onCategoryFilterToggle = (category: TWorkflowTemplateCategory) => {
    setSelectedCategoryFilter((previous) =>
      previous === category ? null : category
    );
  };

  const onTagFilterToggle = (tag: string) => {
    setSelectedTagFilters((previous) =>
      previous.includes(tag)
        ? previous.filter((existing) => existing !== tag)
        : [...previous, tag]
    );
  };

  const handleDelete = async (workflowId: string) => {
    try {
      const res = await deleteWorkflowTemplate(workflowId);
      if (!res.ok) throw new Error('Failed to delete workflow.');
      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'Workflow deleted successfully.',
      });
      mutate();
    } catch {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to delete workflow.',
      });
      throw new Error('Failed to delete workflow.');
    }
  };

  const columns = useMemo(
    () =>
      buildWorkflowListColumns({
        showMetadataControls,
        onWorkflowEdit: (workflow) => {
          setSelectedWorkflow(workflow);
          updateModalVisibility.show();
        },
        onWorkflowOrganize: (workflow) => {
          setSelectedWorkflow(workflow);
          metadataModalVisibility.show();
        },
        onWorkflowDelete: (workflow) => {
          setSelectedWorkflow(workflow);
          deleteModalVisibility.show();
        },
      }),
    [
      showMetadataControls,
      updateModalVisibility,
      metadataModalVisibility,
      deleteModalVisibility,
    ]
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-1">
          <span className="txt-small-medium text-fg-brand">Tools</span>
          <h3 className="text-2xl font-bold text-text-primary">
            Workflow Builder
          </h3>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          Create workflow
        </Button>
      </div>

      {showMetadataControls && (
        <WorkflowListFilters
          categories={presentCategories}
          selectedCategory={selectedCategoryFilter}
          onCategoryToggle={onCategoryFilterToggle}
          tags={allTags}
          selectedTags={selectedTagFilters}
          onTagToggle={onTagFilterToggle}
          onTagFiltersClear={() => setSelectedTagFilters([])}
        />
      )}

      <DataTable
        data={visibleWorkflows}
        searchConfig={{
          isEnabled: true,
          placeholder: 'Search by name...',
          columnsToEnableSearchFor: ['name'],
        }}
        onRowClick={(row) => {
          // DataTable types rows loosely; every row here comes from the IWorkflowTemplate[] above.
          const workflowId = (row as IWorkflowTemplate).id;
          const href = getWorkflowHref
            ? getWorkflowHref(workflowId)
            : `/workflow-builder/${workflowId}`;
          if (href) router.push(href);
        }}
        columnGroups={[
          {
            id: 'workflow-list',
            columns,
          },
        ]}
      />

      <CreateWorkflowModal
        isOpen={showCreateModal}
        getEditorHref={getEditorHref}
        onClose={() => {
          setShowCreateModal(false);
          mutate();
        }}
      />

      {selectedWorkflow && metadataModalVisibility.visible ? (
        <EditWorkflowMetadataModal
          isOpen={metadataModalVisibility.value}
          onClose={() => {
            metadataModalVisibility.off();
            setSelectedWorkflow(null);
          }}
          workflowTemplateId={selectedWorkflow.id}
          workflowName={selectedWorkflow.name}
          initialTags={WorkflowTemplateMetadataHelper.getTags(selectedWorkflow)}
          initialCategory={WorkflowTemplateMetadataHelper.getCategory(
            selectedWorkflow
          )}
          onSaved={() => {
            mutate();
          }}
        />
      ) : null}

      {selectedWorkflow && updateModalVisibility.visible ? (
        <UpdateWorkflowTemplateModal
          isOpen={updateModalVisibility.value}
          onClose={() => {
            updateModalVisibility.off();
            setSelectedWorkflow(null);
          }}
          onSuccess={() => {
            updateModalVisibility.off();
            setSelectedWorkflow(null);
            mutate();
          }}
          workflowTemplateId={selectedWorkflow.id}
          defaultData={selectedWorkflow}
        />
      ) : null}

      <DeleteConfirmationModal
        isOpen={deleteModalVisibility.visible}
        title="Are you sure you want to delete this workflow?"
        message="This action cannot be undone."
        onClose={() => {
          deleteModalVisibility.hide();
          setSelectedWorkflow(null);
        }}
        onConfirm={async () => {
          if (!selectedWorkflow) return;
          await handleDelete(selectedWorkflow.id);
        }}
      />
    </div>
  );
};
